import {
  createItems,
  deleteItem,
  readAssetArrayBuffer,
  readAssetBlob,
  readItem,
  readItems,
  updateItem,
} from "@directus/sdk";
import { directus } from "@/lib/directus";

type DirectusRecord = Record<string, unknown>;
type DirectusFilter = Record<string, unknown>;

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;

  const direct = error as {
    status?: unknown;
    response?: { status?: unknown };
  };

  if (typeof direct.status === "number") return direct.status;
  if (typeof direct.response?.status === "number") return direct.response.status;

  return undefined;
}

function getDirectusErrorCodes(error: unknown): string[] {
  if (!error || typeof error !== "object") return [];

  const directusError = error as {
    errors?: Array<{
      extensions?: { code?: unknown };
      code?: unknown;
    }>;
  };

  return (directusError.errors ?? [])
    .map((entry) => entry.extensions?.code ?? entry.code)
    .filter((code): code is string => typeof code === "string");
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== "object") return "";

  const directusError = error as {
    message?: unknown;
    errors?: Array<{ message?: unknown }>;
  };

  const messages = [
    typeof directusError.message === "string" ? directusError.message : "",
    ...(directusError.errors ?? []).map((entry) =>
      typeof entry.message === "string" ? entry.message : ""
    ),
  ];

  return messages.join(" ");
}

function isAccessDeniedError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 401 || status === 403) return true;

  const permissionCodes = new Set([
    "FORBIDDEN",
    "INVALID_CREDENTIALS",
    "UNAUTHORIZED",
  ]);
  if (getDirectusErrorCodes(error).some((code) => permissionCodes.has(code))) {
    return true;
  }

  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("permission") ||
    message.includes("forbidden") ||
    message.includes("unauthorized") ||
    message.includes("not authorized") ||
    message.includes("no tienes permisos")
  );
}

export const fetchElements = async <T>(
  tableName: string,
  fields: string[] = ["*"],
  federId?: number,
  filter?: DirectusFilter,
  deep?: DirectusRecord
) => {
  try {
    const query: {
      fields: string[];
      filter: DirectusFilter;
      limit: number;
      deep?: DirectusRecord;
    } = {
      fields,
      filter: {},
      limit: -1,
      deep,
    };

    if (typeof federId === "number") {
      query.filter.fondo_feder = { _eq: federId };
    }

    if (filter && Object.keys(filter).length > 0) {
      query.filter = { ...query.filter, ...filter };
    }

    return await directus.request<T[]>(readItems(tableName, query));
  } catch (error) {
    if (isAccessDeniedError(error)) {
      console.warn(`No access to collection ${tableName}; using empty result.`);
      return [];
    }

    console.error(`Error fetching items from ${tableName}:`, error);
    throw error;
  }
};

export const fetchElement = async <T>(
  tableName: string,
  id?: number,
  fields: string[] = ["*"],
  filter?: DirectusFilter
) => {
  try {
    if (typeof id === "number") {
      return await directus.request<T>(
        readItem(tableName, String(id), { fields })
      );
    }

    if (filter && Object.keys(filter).length > 0) {
      const items = await directus.request<T[]>(
        readItems(tableName, { fields, filter, limit: 1 })
      );
      return items?.[0] ?? null;
    }
  } catch (error) {
    if (isAccessDeniedError(error)) {
      console.warn(`No access to item from ${tableName}; using empty result.`);
      return null;
    }

    console.error(`Error fetching item from ${tableName}:`, error);
    throw error;
  }
};

export const deleteElement = async (tableName: string, id: number) => {
  await directus.request(deleteItem(tableName, String(id)));
};

export const readAsset = async (id: string) => {
  return await directus.request(readAssetBlob(id));
};

export const updateElement = async <T extends object>(
  tableName: string,
  id: number,
  changes: Partial<T>
) => {
  await directus.request(updateItem(tableName, String(id), changes));
};

export const saveElements = async <T extends object>(
  tableName: string,
  items: Partial<T>[]
) => {
  return await directus.request(createItems(tableName, items));
};

export const saveElementsType = async <T, TInput extends object = object>(
  tableName: string,
  items: Partial<TInput>[]
): Promise<T[]> => {
  const res = await directus.request(
    createItems(tableName, items, { fields: ["*", "translations.*"] })
  );
  return Array.isArray(res) ? (res as T[]) : ([res] as T[]);
};

export const fetchFileBlobById = async (
  fileId: string
): Promise<Blob | null> => {
  try {
    const buffer = await directus.request(readAssetArrayBuffer(fileId));
    return new Blob([buffer], {
      type: "application/octet-stream",
    });
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
};
